import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "../style/PricePage.module.css";
import { useAuth } from "../context/AuthContext";
import { startPlanCheckoutOrLogin } from '../services/billingservice';

function PricePage() {
    const heroRef = useRef(null);
    const cardsRef = useRef(null);
    const bundleRef = useRef(null);
    const featuresRef = useRef(null);
    const testimonialsRef = useRef(null);
    const faqRef = useRef(null);
    const whyRef = useRef(null);
    const ctaRef = useRef(null);

    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [loadingPlan, setLoadingPlan] = useState(null);
    const [stripeError, setStripeError] = useState("");

    // Prüfen, ob der Benutzer einen bestimmten Plan benötigt
    const urlParams = new URLSearchParams(location.search);
    const neededProduct = urlParams.get('need');
    const productDisplayName = neededProduct === 'chatbot' ? 'Chatbot Service' : 
                              neededProduct === 'anomaly' ? 'Anomalieerkennung' : null;

    // Schreibmaschinen-Effekt
    const typewriterText = "KI-Lösungen für echte Geschäftswirkung.";
    const [typed, setTyped] = useState("");
    useEffect(() => {
        let i = 0;
        let timeout;
        function type() {
            if (i <= typewriterText.length) {
                setTyped(typewriterText.slice(0, i));
                i++;
                timeout = setTimeout(type, 55);
            }
        }
        type();
        return () => clearTimeout(timeout);
    }, []);

    // Intersection-Animationen
    useEffect(() => {
        const observerOptions = {
            threshold: 0.1,
            rootMargin: "0px 0px -50px 0px",
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add(styles.animateIn);
                }
            });
        }, observerOptions);

        const elements = [
            heroRef.current,
            cardsRef.current,
            bundleRef.current,
            featuresRef.current,
            testimonialsRef.current,
            faqRef.current,
            whyRef.current,
            ctaRef.current,
        ];
        elements.forEach((el) => {
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, []);

    // Stripe Testphase starten
    const startTrial = async (plan) => {
        setStripeError('');
        setLoadingPlan(plan);
        try {
            await startPlanCheckoutOrLogin(plan, user, navigate);
        } catch (e) {
            setStripeError('Konnte Stripe Checkout nicht öffnen. Bitte versuchen Sie es später erneut.');
            setLoadingPlan(null);
        }
    };

    return (
        <div className={styles.pageContainer}>
            {/* Dekorativer SVG/Verlauf-Hintergrund */}
            <div className={styles.decorativeBg} aria-hidden="true">
                <svg
                    width="100%"
                    height="100%"
                    viewBox="0 0 1440 600"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={styles.bgSvg}
                >
                    <defs>
                        <linearGradient id="bg-gradient" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="#eaf1fb" />
                            <stop offset="100%" stopColor="#c6d4e8" />
                        </linearGradient>
                        <radialGradient id="blob1" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                            <stop offset="0%" stopColor="#1c3a7a" stopOpacity="0.12" />
                            <stop offset="100%" stopColor="#058744" stopOpacity="0.08" />
                        </radialGradient>
                    </defs>
                    <ellipse cx="300" cy="120" rx="320" ry="120" fill="url(#blob1)" />
                    <ellipse cx="1200" cy="500" rx="260" ry="100" fill="url(#blob1)" />
                </svg>
            </div>

            {/* Hero-Bereich */}
            {false && (
            <section ref={heroRef} className={styles.heroSection}>
                <div className={styles.sectionWrapper}>
                    <div className={styles.heroGraphics}>
                        <div className={styles.heroCircle1}></div>
                        <div className={styles.heroCircle2}></div>
                        <div className={styles.heroDots}></div>
                    </div>
                    <h1 className={styles.heroHeadline}>Einfache & Transparente Preisgestaltung</h1>
                    <div className={styles.typewriterLine} aria-label={typewriterText}>
                        {typed}
                        <span className={styles.typewriterCursor}>|</span>
                    </div>
                    <p className={styles.heroTrial}>
                        Testen Sie unsere leistungsstarken Tools 1 Monat kostenlos – keine Kreditkarte erforderlich!
                    </p>
                    <p className={styles.heroSubheadline}>
                        Wählen Sie die Tools, die am besten für Ihr Unternehmen funktionieren – beginnen Sie mit einem oder beiden, wechseln Sie jederzeit.
                    </p>
                    {stripeError && (
                        <div
                            style={{
                                color: "red",
                                marginTop: "0.75rem",
                                fontWeight: 600,
                                textAlign: "center",
                            }}
                        >
                            {stripeError}
                        </div>
                    )}
                </div>
            </section>
            )}

            {/* Banner: Plan erforderlich */}
            {false && neededProduct && productDisplayName && (
                <section className={styles.needPlanBanner}>
                    <div className={styles.sectionWrapper}>
                        <div className={styles.bannerContent}>
                            <div className={styles.bannerIcon}>🔒</div>
                            <div className={styles.bannerText}>
                                <h3>Sie benötigen einen Plan für den Zugang zu {productDisplayName}</h3>
                                <p>Starten Sie Ihre kostenlose 30-tägige Testphase, um alle Funktionen freizuschalten und vollen Zugriff zu erhalten.</p>
                            </div>
                            <button
                                className={styles.bannerButton}
                                onClick={() => startTrial(neededProduct)}
                                disabled={loadingPlan === neededProduct}
                            >
                                {loadingPlan === neededProduct ? "Weiterleiten…" : "Kostenlose Testphase starten"}
                            </button>
                        </div>
                    </div>
                </section>
            )}

            {/* Einfache Info (Werbung entfernt) */}
            <section className={styles.pricingCardsSection}>
                <div className={styles.sectionWrapper}>
                    <div style={{
                        background: '#fff',
                        borderRadius: '16px',
                        padding: '24px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
                    }}>
                        <h2 style={{marginTop: 0}}>Preise auf Anfrage</h2>
                        <p>Bitte kontaktieren Sie uns für ein individuelles Angebot.</p>
                        <a href="/contact" className={styles.bannerButton}>Kontakt aufnehmen</a>
                    </div>
                </div>
            </section>

            {/* Preiskarten-Bereich (ausgeblendet) */}
            {false && (
            <section ref={cardsRef} className={styles.pricingCardsSection}>
                <div className={styles.sectionWrapper}>
                    <div className={styles.sectionGraphics}>
                        <div className={styles.cardsBackground}></div>
                    </div>
                    <div className={styles.pricingCardsGrid}>
                        {/* Chatbot Service Card */}
                        <div className={styles.pricingCard}>
                            <div className={styles.cardGraphics}>
                                <div className={styles.cardIcon}>
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <circle cx="12" cy="12" r="10" />
                                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                                        <line x1="12" y1="17" x2="12.01" y2="17" />
                                    </svg>
                                </div>
                                <div className={styles.cardGlow}></div>
                            </div>
                            <div className={styles.cardHeader}>🔹 Chatbot Service</div>
                            <div className={styles.cardPrice}>
                                €20<span className={styles.pricePeriod}>/Monat</span>
                            </div>
                            <div className={styles.cardTrial}>(nach kostenloser 1-monatiger Testphase)</div>
                            <ul className={styles.featuresList}>
                                <li>🤖 Benutzerdefinierter KI-Chatbot mit natürlichem Sprachverständnis</li>
                                <li>
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        style={{
                                            marginRight: "8px",
                                            display: "inline-block",
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        <path d="M18 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                    </svg>
                                    Vortrainiert für Ihre Domäne mit kontinuierlichem Lernen
                                </li>
                                <li>
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        style={{
                                            marginRight: "8px",
                                            display: "inline-block",
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                        <polyline points="15,3 21,3 21,9" />
                                        <line x1="10" y1="14" x2="21" y2="3" />
                                    </svg>
                                    Einfache Integration in Ihre Website oder App
                                </li>
                                <li>🧠 Kontextbewusste Unterhaltungen</li>
                                <li>🛠️ Admin-Dashboard für Chat-Verlauf, Absichten und Analytics</li>
                                <li>🔒 Sicherheit und Compliance auf Enterprise-Niveau</li>
                                <li>📊 Erweiterte Analysen und Leistungsmetriken</li>
                                <li>🔄 24/7 automatisierter Kundensupport</li>
                            </ul>
                            <button
                                className={styles.ctaButton}
                                onClick={() => startTrial("chatbot")}
                                disabled={loadingPlan === "chatbot"}
                            >
                                👉 {loadingPlan === "chatbot" ? "Weiterleiten…" : "Kostenlose Testphase starten"}
                            </button>
                        </div>

                        {/* Anomalieerkennung Card */}
                        <div className={styles.pricingCard}>
                            <div className={styles.cardGraphics}>
                                <div className={styles.cardIcon}>
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <circle cx="12" cy="12" r="10" />
                                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                                        <line x1="12" y1="17" x2="12.01" y2="17" />
                                    </svg>
                                </div>
                                <div className={styles.cardGlow}></div>
                            </div>
                            <div className={styles.cardHeader}>🔹 Anomalieerkennung</div>
                            <div className={styles.cardPrice}>
                                €20<span className={styles.pricePeriod}>/Monat</span>
                            </div>
                            <div className={styles.cardTrial}>(nach kostenloser 1-monatiger Testphase)</div>
                            <ul className={styles.featuresList}>
                                <li>
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        style={{
                                            marginRight: "8px",
                                            display: "inline-block",
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        <path d="M3 3h18v18H3V3z" />
                                        <path d="M8 6h8v2H8V6zm0 4h8v2H8v-2zm0 4h8v2H8v-2z" />
                                    </svg>
                                    Echtzeit-Anomalieerkennung für Systemlogs oder Metriken
                                </li>
                                <li>
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        style={{
                                            marginRight: "8px",
                                            display: "inline-block",
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        <circle cx="12" cy="12" r="10" />
                                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                                        <line x1="12" y1="17" x2="12.01" y2="17" />
                                    </svg>
                                    Intelligente Visualisierungen zur Hervorhebung von Abweichungen
                                </li>
                                <li>
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        style={{
                                            marginRight: "8px",
                                            display: "inline-block",
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        <circle cx="12" cy="12" r="10" />
                                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                                        <line x1="12" y1="17" x2="12.01" y2="17" />
                                    </svg>
                                    Einfache Integration mit bestehenden Monitoring-Tools
                                </li>
                                <li>
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        style={{
                                            marginRight: "8px",
                                            display: "inline-block",
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        <circle cx="12" cy="12" r="10" />
                                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                                        <line x1="12" y1="17" x2="12.01" y2="17" />
                                    </svg>
                                    Konfigurierbare Schwellenwerte und Warnungen
                                </li>
                                <li>
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        style={{
                                            marginRight: "8px",
                                            display: "inline-block",
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        <rect x="3" y="3" width="18" height="18" rx="2" />
                                        <path d="M3 9h18" />
                                        <path d="M9 21V9" />
                                        <path d="M15 21V9" />
                                    </svg>
                                    Tägliche/wöchentliche Berichte per E-Mail oder Dashboard
                                </li>
                                <li>
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        style={{
                                            marginRight: "8px",
                                            display: "inline-block",
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        <rect x="3" y="3" width="18" height="18" rx="2" />
                                        <path d="M3 9h18" />
                                        <path d="M9 21V9" />
                                        <path d="M15 21V9" />
                                    </svg>
                                    SOC 2 Type II konforme Datenbehandlung
                                </li>
                                <li>
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        style={{
                                            marginRight: "8px",
                                            display: "inline-block",
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        <path d="M3 3h18v18H3V3z" />
                                        <path d="M8 6h8v2H8V6zm0 4h8v2H8v-2zm0 4h8v2H8v-2z" />
                                    </svg>
                                    Machine Learning-gestützte Vorhersagemodelle
                                </li>
                                <li>
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        style={{
                                            marginRight: "8px",
                                            display: "inline-block",
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        <circle cx="12" cy="12" r="10" />
                                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                                        <line x1="12" y1="17" x2="12.01" y2="17" />
                                    </svg>
                                    Sofortige Warnung mit Eskalationsprotokollen
                                </li>
                            </ul>
                            <button
                                className={styles.ctaButton}
                                onClick={() => startTrial("anomaly")}
                                disabled={loadingPlan === "anomaly"}
                            >
                                👉 {loadingPlan === "anomaly" ? "Weiterleiten…" : "Kostenlose Testphase starten"}
                            </button>
                        </div>
                    </div>
                </div>
            </section>
            )}

            {/* Bundle-Angebot (ausgeblendet) */}
            {false && (
            <section ref={bundleRef} className={styles.bundleSection}>
                <div className={styles.sectionWrapper}>
                    <div className={styles.bundleGraphics}>
                        <div className={styles.bundleStars}>
                            <div className={styles.star1}>⭐</div>
                            <div className={styles.star2}>⭐</div>
                            <div className={styles.star3}>⭐</div>
                        </div>
                    </div>
                    <div className={styles.bundleBox}>
                        <div className={styles.bundleHeadline}>💡 Beide Tools nutzen?</div>
                        <div className={styles.bundleText}>
                            Erhalten Sie nahtlose Integration und kombinierte Einblicke, wenn Sie Chatbot + Anomalieerkennung zusammen nutzen.
                        </div>
                        <div className={styles.bundlePreiseow}>
                            <span className={styles.bundleCheck}>✅</span> Beide Produkte →{" "}
                            <span className={styles.bundlePrice}>€40/Monat</span> nach kostenloser Testphase
                        </div>
                        <div className={styles.bundleCancel}>✅ Jederzeit kündbar — keine versteckten Gebühren</div>
                        <button
                            className={styles.ctaButton}
                            style={{ marginTop: "1.5rem" }}
                            onClick={() => startTrial("both")}
                            disabled={loadingPlan === "both"}
                        >
                            👉 {loadingPlan === "both" ? "Weiterleiten…" : "Kostenlose Testphase für beide starten"}
                        </button>
                    </div>
                </div>
            </section>
            )}

            {/* Enterprise Features (ausgeblendet) */}
            {false && (
            <section ref={featuresRef} className={styles.featuresSection}>
                <div className={styles.sectionWrapper}>
                    <h2 className={styles.featuresHeadline}>Enterprise-Level Features</h2>
                    <div className={styles.featuresGrid}>
                        <div className={styles.featureCard}>
                            <div className={styles.featureIcon}>🔒</div>
                            <h3>Sicherheit & Compliance</h3>
                            <p>
                                SOC 2 Type II zertifiziert, DSGVO-konform, Ende-zu-Ende-Verschlüsselung und Sicherheitsprotokolle auf Enterprise-Niveau.
                            </p>
                        </div>
                        <div className={styles.featureCard}>
                            <div className={styles.featureIcon}>📊</div>
                            <h3>Erweiterte Analysen</h3>
                            <p>
                                Umfassende Berichte, Echtzeit-Dashboards und actionable Einblicke zur Optimierung Ihrer KI-Performance.
                            </p>
                        </div>
                        <div className={styles.featureCard}>
                            <div className={styles.featureIcon}>🔄</div>
                            <h3>99,9% Verfügbarkeits-SLA</h3>
                            <p>Garantierte Zuverlässigkeit mit Enterprise-Level Infrastruktur und 24/7 Monitoring-Support.</p>
                        </div>
                        <div className={styles.featureCard}>
                            <div className={styles.featureIcon}>👥</div>
                            <h3>Dedizierter Support</h3>
                            <p>
                                Prioritätssupport mit dedizierten Kundenbetreuern und technischen Spezialisten für Enterprise-Kunden.
                            </p>
                        </div>
                        <div className={styles.featureCard}>
                            <div className={styles.featureIcon}>🔧</div>
                            <h3>Maßgeschneiderte Integration</h3>
                            <p>Nahtlose Integration in Ihre bestehenden Tools und Workflows über unsere umfassende API.</p>
                        </div>
                        <div className={styles.featureCard}>
                            <div className={styles.featureIcon}>📈</div>
                            <h3>Skalierbare Architektur</h3>
                            <p>
                                Entwickelt, um mit Ihrem Unternehmenswachstum zu skalieren und Millionen von Interaktionen und Datenpunkten zu verarbeiten.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
            )}

            {/* Kundenstimmen (ausgeblendet) */}
            {false && (
            <section ref={testimonialsRef} className={styles.testimonialsSection}>
                <div className={styles.sectionWrapper}>
                    <h2 className={styles.testimonialsHeadline}>Vertrauen von Branchenführern</h2>
                    <div className={styles.testimonialsGrid}>
                        <div className={styles.testimonialCard}>
                            <div className={styles.testimonialContent}>
                                <p>
                                    "AWN AI hat unseren Kundensupport mit ihrem intelligenten Chatbot transformiert. Die Antwortzeiten haben sich um
                                    80% verbessert und die Kundenzufriedenheit ist erheblich gestiegen."
                                </p>
                            </div>
                            <div className={styles.testimonialAuthor}>
                                <div className={styles.authorInfo}>
                                    <h4>Sarah Müller</h4>
                                    <span>CTO, TechCorp GmbH</span>
                                </div>
                            </div>
                        </div>
                        <div className={styles.testimonialCard}>
                            <div className={styles.testimonialContent}>
                                <p>
                                    "Das Anomalieerkennung-System half uns dabei, kritische Probleme zu identifizieren, bevor sie unsere Services beeinträchtigten.
                                    Es ist wie ein 24/7 Sicherheitswächter für unsere Infrastruktur."
                                </p>
                            </div>
                            <div className={styles.testimonialAuthor}>
                                <div className={styles.authorInfo}>
                                    <h4>Michael Weber</h4>
                                    <span>Head of DevOps, DataFlow Solutions</span>
                                </div>
                            </div>
                        </div>
                        <div className={styles.testimonialCard}>
                            <div className={styles.testimonialContent}>
                                <p>
                                    "Die Implementierung beider Tools zusammen gab uns beispiellose Einblicke in unsere Abläufe. Der ROI war
                                    sofort sichtbar und wächst Monat für Monat weiter."
                                </p>
                            </div>
                            <div className={styles.testimonialAuthor}>
                                <div className={styles.authorInfo}>
                                    <h4>Dr. Anna Schmidt</h4>
                                    <span>VP Engineering, Innovation Labs</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            )}

            {/* FAQ (ausgeblendet) */}
            {false && (
            <section ref={faqRef} className={styles.faqSection}>
                <div className={styles.sectionWrapper}>
                    <h2 className={styles.faqHeadline}>Häufig gestellte Fragen</h2>
                    <div className={styles.faqGrid}>
                        <div className={styles.faqItem}>
                            <h3>Was ist in der kostenlosen Testphase enthalten?</h3>
                            <p>
                                Vollzugang zu allen Features für 30 Tage, keine Kreditkarte erforderlich. Sie können jederzeit während oder nach der Testphase auf einen bezahlten Plan upgraden.
                            </p>
                        </div>
                        <div className={styles.faqItem}>
                            <h3>Kann ich jederzeit kündigen?</h3>
                            <p>
                                Ja, Sie können Ihr Abonnement jederzeit ohne Strafen oder versteckte Gebühren kündigen. Ihr Service wird bis zum Ende Ihres Abrechnungszeitraums fortgesetzt.
                            </p>
                        </div>
                        <div className={styles.faqItem}>
                            <h3>Bieten Sie Enterprise-Preise an?</h3>
                            <p>
                                Ja, wir bieten maßgeschneiderte Enterprise-Pläne mit dediziertem Support, erweiterten Features und Mengenrabatten für größere Organisationen an.
                            </p>
                        </div>
                        <div className={styles.faqItem}>
                            <h3>Welche Art von Support bieten Sie?</h3>
                            <p>
                                Wir bieten umfassenden Support einschließlich Dokumentation, Video-Tutorials, Live-Chat, E-Mail-Support und dedizierter Kundenbetreuung für Enterprise-Kunden.
                            </p>
                        </div>
                        <div className={styles.faqItem}>
                            <h3>Sind meine Daten sicher?</h3>
                            <p>
                                Absolut. Wir verwenden Enterprise-Level Verschlüsselung, SOC 2 Type II Compliance und DSGVO-konforme Datenverarbeitung, um sicherzustellen, dass Ihre Daten immer sicher sind.
                            </p>
                        </div>
                        <div className={styles.faqItem}>
                            <h3>Kann ich mit meinen bestehenden Tools integrieren?</h3>
                            <p>
                                Ja, wir bieten umfassende APIs und Integrationen mit beliebten Plattformen wie Slack, Teams, Zapier und benutzerdefinierten Webhook-Support.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
            )}

            {/* Warum Uns Wählen (ausgeblendet) */}
            {false && (
            <section ref={whyRef} className={styles.whySection}>
                <div className={styles.sectionWrapper}>
                    <div className={styles.whyGraphics}>
                        <div className={styles.whyLines}></div>
                    </div>
                    <h2 className={styles.whyHeadline}>Warum AWN AI wählen?</h2>
                    <ul className={styles.whyList}>
                        <li>🧠 KI, die für Sie arbeitet — Intelligente, sich selbst verbessernde Systeme, die Zeit sparen und die Effizienz steigern.</li>
                        <li>🕒 1-monatige kostenlose Testphase mit vollständigem Feature-Zugang</li>
                        <li>🧩 Einfache Einrichtung — In Minuten starten mit minimaler Konfiguration.</li>
                        <li>🔒 Enterprise-Level Sicherheit mit SOC 2 Type II Compliance</li>
                        <li>📊 Erweiterte Analysen und umsetzbare Einblicke</li>
                        <li>🚀 99,9% Verfügbarkeits-SLA mit 24/7 Monitoring</li>
                        <li>👥 Dediziertes Support-Team für Enterprise-Kunden</li>
                        <li>💰 Transparente Preisgestaltung ohne versteckte Gebühren</li>
                    </ul>
                </div>
            </section>
            )}

            {/* Finaler Aufruf zum Handeln (ausgeblendet) */}
            {false && (
            <section ref={ctaRef} className={styles.finalCtaSection}>
                <div className={styles.sectionWrapper}>
                    <div className={styles.ctaGraphics}>
                        <div className={styles.ctaRings}>
                            <div className={styles.ring1}></div>
                            <div className={styles.ring2}></div>
                            <div className={styles.ring3}></div>
                        </div>
                    </div>
                    <h2 className={styles.finalCtaHeadline}>Bereit, Ihr Unternehmen mit KI zu revolutionieren?</h2>
                    <p className={styles.finalCtaSubtext}>
                        Schließen Sie sich Tausenden von Unternehmen an, die bereits AWN AI nutzen, um ihre Abläufe zu transformieren.
                    </p>
                    <div className={styles.ctaButtons}>
                        <button
                            className={styles.primaryCtaButton}
                            onClick={() => startTrial("both")}
                            disabled={loadingPlan === "both"}
                        >
                            {loadingPlan === "both" ? "Weiterleiten…" : "Kostenlose Testphase starten"}
                        </button>
                        <button className={styles.secondaryCtaButton}>Demo planen</button>
                    </div>
                    {stripeError && (
                        <div
                            style={{
                                color: "red",
                                marginTop: "0.75rem",
                                fontWeight: 600,
                                textAlign: "center",
                            }}
                        >
                            {stripeError}
                        </div>
                    )}
                </div>
            </section>
            )}
        </div>
    );
}

export default PricePage;